import { Fragment, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { ExportBar } from "@/components/calc/ExportBar";
import { CalcHistory } from "@/components/calc/CalcHistory";
import { CALC_PRINT_CSS } from "@/components/calc/ResultsTable";
import { stepsToText } from "@/lib/calc/steps";
import { recordCalculation } from "@/lib/calc/history";
import { toCsv } from "@/lib/csv";
import { REFERENCE_RANGES, romanRange, romanSteps, toRomanVinculum, type RomanRow } from "./roman";

const COLUMNS = 4;

/** The rows split into COLUMNS blocks side by side (1-25, 26-50, ...), so 100 rows fit in 25 table rows. */
function blocks(rows: RomanRow[]): (RomanRow | undefined)[][] {
  const per = Math.ceil(rows.length / COLUMNS);
  return Array.from({ length: per }, (_, i) => Array.from({ length: COLUMNS }, (_, c) => rows[c * per + i]));
}

/**
 * Under the Roman numeral result (B-0114): the working for the number being converted and a reference table (1 to 100, or years).
 * Export goes through the shared gated bar: Copy, CSV and Print each take the visit's one trial use only when delivered.
 * Loaded on demand (React.lazy).
 */
export default function RomanExtras({ n, gate }: { n: number | null; gate: () => Promise<boolean> }) {
  const [rangeId, setRangeId] = useState<(typeof REFERENCE_RANGES)[number]["id"]>("1-100");
  const range = REFERENCE_RANGES.find((r) => r.id === rangeId)!;
  const rows = useMemo(() => romanRange(range.from, range.to), [range]);
  const grid = useMemo(() => blocks(rows), [rows]);
  const steps = useMemo(() => (n ? romanSteps(n) : []), [n]);

  useEffect(() => {
    if (!n) return;
    const id = setTimeout(() => recordCalculation("roman-numeral-converter", `${n.toLocaleString("en-US")} = ${toRomanVinculum(n)}`), 800);
    return () => clearTimeout(id);
  }, [n]);

  const csv = toCsv(rows.map((r) => ({ n: r.n, roman: r.roman })), [{ key: "n", header: "Number" }, { key: "roman", header: "Roman numeral" }]);
  const text = [
    `Roman numerals, ${range.label}`,
    ...(n ? ["", `Working for ${n.toLocaleString("en-US")}`, stepsToText(steps)] : []),
    "", ["Number", "Roman numeral"].join("\t"), ...rows.map((r) => `${r.n}\t${r.roman}`), "", "Calculated with FreeDailyPro.com",
  ].join("\n");

  return (
    <div className="mt-8 space-y-6 calc-print-area" data-testid="roman-extras">
      <style>{CALC_PRINT_CSS}</style>
      {n ? <CalcSteps steps={steps} title={`How ${n.toLocaleString("en-US")} is written`} /> : null}
      <section aria-label="Roman numeral reference table" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 no-print print:hidden" role="group" aria-label="Choose a reference table">
          {REFERENCE_RANGES.map((r) => (
            <Button key={r.id} type="button" size="sm" variant={r.id === rangeId ? "default" : "outline"} aria-pressed={r.id === rangeId} onClick={() => setRangeId(r.id)}>{r.label}</Button>
          ))}
        </div>
        <h2 className="text-lg font-semibold">Roman numerals chart 1–100 and key years</h2>
        <h3 className="text-base font-semibold">Roman numerals, {range.label}</h3>
        <div className="overflow-x-auto rounded-lg border" tabIndex={0} aria-label={`Roman numerals ${range.label}, scrollable`}>
          <table className="w-full text-sm" data-testid="roman-table">
            <caption className="sr-only">Numbers {range.label} with their Roman numerals</caption>
            <thead>
              <tr className="border-b text-left text-xs text-muted-foreground">
                {Array.from({ length: COLUMNS }, (_, c) => (
                  <th key={c} colSpan={2} scope="colgroup" className="p-2">{grid[0][c] ? `${grid[0][c]!.n} to ${rows[Math.min(rows.length, (c + 1) * grid.length) - 1].n}` : ""}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.map((row, i) => (
                <tr key={i} className="border-b last:border-0">
                  {row.map((cell, c) => cell ? (
                    <Fragment key={c}><th scope="row" className="p-2 text-right font-normal text-muted-foreground tabular-nums">{cell.n}</th><td className="p-2 font-medium">{cell.roman}</td></Fragment>
                  ) : <Fragment key={c}><td /><td /></Fragment>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <ExportBar gate={gate} text={text} csv={csv} filename={`roman-numerals-${range.id}.csv`} label="table" />
      <CalcHistory tool="roman-numeral-converter" />
    </div>
  );
}
